<!-- apps/web/src/components/shell/NotificationsDropdown.vue -->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import IconX from '@/components/icons/IconX.vue';

dayjs.extend(relativeTime);

// Local type until OpenAPI gen runs (will be replaced in Task 18 with the real type)
export interface NotificationItem {
  id: string;
  kind: string;
  entityType: string;
  entityId: string;
  title: string;
  body: string;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

const props = defineProps<{
  notifications: NotificationItem[];
  unreadCount: number;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'mark-read', id: string): void;
  (e: 'mark-all-read'): void;
}>();

const rootRef = ref<HTMLElement | null>(null);
onClickOutside(rootRef, () => emit('close'));

const router = useRouter();
const displayed = computed(() => props.notifications.slice(0, 10));

function routeForNotification(item: NotificationItem): string {
  if (item.kind === 'ResidentApprovalPending') return '/residents/approvals';
  return `/issues/${item.entityId}`;
}

function handleRowClick(item: NotificationItem) {
  emit('mark-read', item.id);
  void router.push(routeForNotification(item));
  emit('close');
}

function dotColor(kind: string): string {
  return kind === 'CriticalIssueIngested' ? 'bg-amber-600' : 'bg-navy-300';
}
</script>

<template>
  <div
    ref="rootRef"
    class="absolute right-0 top-[calc(100%+8px)] w-80 max-h-[420px] flex flex-col
           bg-surface border border-border rounded-xl shadow-lg z-50 overflow-hidden"
    data-testid="notifications-dropdown"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
      <span class="font-medium text-sm text-fg">Notifications</span>
      <div class="flex items-center gap-2">
        <button
          v-if="unreadCount > 0"
          type="button"
          class="text-xs text-navy-600 hover:text-navy-800 transition-colors"
          data-testid="mark-all-read"
          @click="emit('mark-all-read')"
        >
          Mark all read
        </button>
        <button
          type="button"
          class="w-6 h-6 grid place-items-center text-muted hover:text-fg rounded transition-colors"
          aria-label="Close notifications"
          @click="emit('close')"
        >
          <IconX class="w-3.5 h-3.5" />
        </button>
      </div>
    </div>

    <!-- List -->
    <div class="flex-1 overflow-y-auto">
      <!-- Empty state -->
      <div
        v-if="displayed.length === 0"
        class="py-8 text-center text-sm text-muted"
        data-testid="empty-state"
      >
        You're all caught up
      </div>

      <!-- Rows -->
      <button
        v-for="item in displayed"
        :key="item.id"
        type="button"
        class="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-canvas-soft transition-colors"
        :class="item.isRead ? 'bg-surface' : 'bg-canvas-soft'"
        :data-testid="`notification-row-${item.id}`"
        @click="handleRowClick(item)"
      >
        <!-- Kind dot -->
        <span
          class="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
          :class="dotColor(item.kind)"
        />
        <!-- Content -->
        <div class="flex-1 min-w-0">
          <p
            class="text-sm truncate"
            :class="item.isRead ? 'font-normal text-fg-soft' : 'font-medium text-fg'"
          >
            {{ item.title }}
          </p>
          <p class="text-xs text-muted truncate">{{ item.body }}</p>
          <p class="font-mono tabular-nums text-xs text-muted mt-0.5">
            {{ dayjs(item.createdAt).fromNow() }}
          </p>
        </div>
        <!-- Unread dot -->
        <span
          v-if="!item.isRead"
          class="mt-2 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0"
          aria-hidden="true"
        />
      </button>
    </div>

    <!-- Footer -->
    <div class="flex-shrink-0 border-t border-border px-4 py-2.5">
      <span
        class="text-xs text-navy-600 cursor-not-allowed opacity-50"
        title="Full notifications page coming soon"
      >
        View all notifications
      </span>
    </div>
  </div>
</template>
